<template>
	<!-- 
		# MainCard

		V.1.3

		Limpieza general y optimizaciones menores

		V.1.2

		Buscador automático
		
		V.1.1

		Ahora la configuración de origen de datos y origen de índice, 
		junto a la coniguración de campos, se define fuera del objeto de exportación

		V.1.0

		Para la integración de imágenes requiere 
		**img-input-autoload** configurado en elements y
		el controlador UiController.php y UiModel.php en la API Base. **Además hace uso del 
		paginador para javascript implementado en PaginateController.php**

		Para la actualización de datos hace uso de StorageController.php 

		@Author Daniel Lepe 2021
	-->

	<b-card id="MenusMainCard" no-body>

		<!-- CARD HEADER -->
		<b-row align-h="end" slot="header">

			<b-col>
				<div tabindex="-1" role="group">
					<div role="group" class="input-group">
						<input type="text" v-model="term" @keyup.enter="refresh()" :placeholder="labelsCfg.searchPlaceholder"
							class="form-control" />
						<div class="input-group-append" v-if="!!term">
							<b-button type="button" variant="light" @click="term = null; refresh()">
								<i class="fa fa-times"></i>
							</b-button>
							<b-button type="button" :variant="variant" @click="refresh()">
								<i class="fa fa-search"></i>
							</b-button>
						</div>
					</div>
				</div>
			</b-col>

			<b-col md="4" v-if="!hideNew">
				<b-button-group class="mb-0">
					<b-button :variant="variant" @click="$refs.MenuMainModal.openForNew()"> {{labelsCfg.newLabel}} </b-button>
					<slot name="mainButtons" />
				</b-button-group>
			</b-col>

		</b-row>
		<!-- CARD HEADER -->

		<div v-if="isLoading">
			<div class="p-1 text-center">
				<spinner />
			</div>
		</div>

		<!-- DATA TEMPLATE -->
		<template v-else-if="parseInt(data.count)">

			<b-table :fields="cFields" responsive striped class="mb-0" :items="items" small>

				<!-- START OF AVATAR SAMPLES -->
				<template #cell(check)="data">
					<b-form-radio @change="$emit('input', data.item.id_pk); $emit('titleChange', data.item.nombre_plan);" :value="data.item.id_pk" v-model="workingValue" />
				</template>
				<!-- END OF AVATAR SAMPLES -->

				<!-- START OF AVATAR SAMPLES -->
				<template #cell(nombre_plan)="data">
					<img-portal :src="data.item.imagen" />
					{{data.value}}
				</template>
				<!-- END OF AVATAR SAMPLES -->

				<!-- START OF CERTIFICATION DATA -->
				<template #cell(certificacion)="data">
					<div class="inline text-left nowarp">
						<template v-if="data.item.certificacion_estatus == 'Certificado'">
							<strong class="text-primary">
								<i class="fa fa-check"></i> Certificado
							</strong>
						</template>
						<template v-else>
							Sin revisar
						</template>
					</div>
				</template>
				<!-- END OF CERTIFICATION DATA -->
				
				<!-- START TEMPLATES BASICOS DE DATOS -->
				<template #cell(duraciones)="data">
					<div class="duracion">
						<i class="fa fa-clock-o"></i>
						{{moment().hour(0).minute(0).add(parseInt(data.item.tiempo_coccion) + parseInt(data.item.tiempo_preparacion), 'minute').format('HH:mm')}}
					</div>
				</template>

				<template #cell(desayuno)="data">
					<tiny-check :value="data.value" />
				</template>

				<template #cell(comida)="data">
					<tiny-check :value="data.value" />
				</template>

				<template #cell(cena)="data">
					<tiny-check :value="data.value" />
				</template>

				<template #cell(created)="data">{{data.value | humanizar_fecha}} <br> <small>(Última actualizacion {{data.item.modified | humanizar_diferencia}})</small> </template>
				<template #cell(modified)="data">{{data.value | humanizar_diferencia}} <br> <small>(Última actualizacion {{data.item.modified | humanizar_diferencia}})</small> </template>
				<!-- END TEMPLATES BASICOS DE DATOS -->

				<!-- START OF AVATAR SAMPLES -->
				<template #cell(receta)="data">
					<img-portal :src="data.item.imagen" />
					{{data.item.nombre}}
				</template>
				<!-- END OF AVATAR SAMPLES -->

				<!-- START OF BOOLEAN SAMPLE -->
				<template #cell(disponible)="data">
					<div class="align-center">
						<!-- <b-button size="sm" variant="primary" v-if="data.value == '1'"> -->
							<i v-if="data.value == '1'" class="fa text-primary fa-check"></i>
						<!-- </b-button> -->
						<!-- <b-button size="sm" v-else> -->
							<i v-else class="fa fa-minus"></i>
						<!-- </b-button> -->
					</div>
				</template>
				<!-- END OF BOOLEAN SAMPLE -->

				<!-- START OF TOOLING -->
				<template #cell(_)="data">
					<b-button @click="$refs.MenuMainModal.loadItem(data.item.id_pk)" size="sm" :variant="variant"> <i class="fa fa-fw fa-cog"></i> </b-button>
				</template>
				<!-- END OF TOOLING -->

			</b-table>

			<b-row slot="footer" align-v="center">

				<b-col>
					<strong>Página {{data.page}} de {{Math.ceil(data.count / pageSize)}}</strong> 
					(Se muestran <strong>{{items.length}} registros</strong> 
					de un total de {{data.count}})
				</b-col>

				<b-col>
					<b-pagination
						class="mt-3"
						v-model="data.page"
						:total-rows="data.count"
						:per-page="pageSize"
						size="sm"
						align="right"
						prev-text="Anterior"
						next-text="Siguiente"
					/>
				</b-col>
			</b-row>

		</template>
		<!-- DATA TEMPLATE -->

		<!-- EMPTY JUMBOTRON -->
		<b-jumbotron class="mb-0" v-else>
			<template #header>{{labelsCfg.emptyHeader}}</template>
			<template #lead>{{labelsCfg.emptyLead}}</template>
		</b-jumbotron>
		<!-- EMPTY JUMBOTRON -->

		<MenuMainModal ref="MenuMainModal" :args="args"/>

	</b-card>

</template>

<script>
	import axios from 'axios'
	import _ from 'lodash'
	import fkSelect from "@/views/elements/fk-select.vue";
	import imgInputAutoload from "@/views/elements/img-input-autoload.vue"
	import moment from 'moment'
	import spinner from "../elements/spinner.vue";
	import MenuMainModal from './MenuMainModal.vue'

	let labelsCfg = {
		emptyHeader: 'Lista vacía',
		emptyLead: '',
		searchPlaceholder: 'Busca por menú, propietario o receta...',
		newLabel: 'Nuevo menú',
		btnDelete: 'Eliminar',
		btnCancel: 'Cancelar',
		btnApply: 'Aplicar',
		btnIsProcessing: 'Procesando...',
	}

	let indexSource = 'nutricion/menus'; // Para indexar; 
		

	let fields = [ // DEJAR VACÍO PARA MOSTRAR TODO
		{key: 'nombre_plan', label: 'Plan'} ,
		{key: 'propietario', label: 'Propietario'} ,
		// {key: 'miembros_', label: 'Miembros'} ,
		{key: 'ultima_fecha', label: 'Última actualización'} ,
		{key: 'certificacion', label: 'Certificación'} ,
		{key: '_'}
	]
	
	export default {
		name: 'MenusMainCard',
		props: {
			value: {
				type: [String, Number],
				default: ''
			},
			seleccionable: {
				type: Boolean,
				default: false
			},
			hideNew: {
				type: Boolean,
				default: false
			},
			pageSize: {
				type: [Number, String],
				default: 25
			},
			args: {
				type: Object,
				default: () => { return {} }
			},
		},
		components: {
			fkSelect, imgInputAutoload, MenuMainModal, spinner
		},
		computed: {
			cFields(){
				let r = []
				if(this.seleccionable)
					r.push({key: 'check', label: ''});
				return _.concat( this.fields, r)
			},
			items(){
				return _.map(this.data.rows, item => {
					item._ = ' '
					return item
				})
			},

			// COMPUTA LA URI
			sourceURI() {
				let init = `${this.indexSource}/${this.pageSize}/${this.data.page}`;
				if (!!this.term) {
					init += "/" + this.term;
				} else if (!this.term || this.term == "s") {
					init += "/@";
				}
				init += '/' + this.objectToUrlString(this.args)
				return init.trim().replace(/[ ]/g, '_');
			},
		},
		data: () => {
			return {
				moment,
				isLoading: false,
				isProcessing: false,
				term: null,
				variant: "primary",
				indexSource,
				fields,
				labelsCfg: _.cloneDeep(labelsCfg),
				workingValue: null,
				data: {
					rows: [],
					count: 0,
					page: 1
				}
			}
		},
		methods: {
			objectToUrlString(obj) {
				return Object.entries(obj)
					.map(([key, value]) => {
						const valueFixed = value.replace(/[ ]/g, '_');
						return `${encodeURIComponent(key)}=${encodeURIComponent(valueFixed)}`;
					})
					.join(':');
			},
			refresh() {
				this.isLoading = true;
				this.loadUp().then(() => {
					this.isLoading = false;
				});
			},
			loadUp(){
				this.$emit('input', '');
				this.$emit('titleChange', '');
				return new Promise(success => {
					axios.get(this.sourceURI).then(response => {
						if(typeof response.data.rows != undefined){
							this.data.rows = response.data.rows
							this.data.count = response.data.count
							success()
						}
					})
				})
			}
		},
		mounted() {
			this.isLoading = true;
			this.loadUp().then(()=>{
				this.isLoading = false;
			})
		},
		watch: {
			'data.page'(){
				this.isLoading = true;
				this.loadUp().then(()=>{
					this.isLoading = false;
				})
			}
		}
	}

</script>

<style lang="less">
	#MenusMainCard {
		.duracion {
			transform: scale(1.5);
		}
		.table {
			thead  {
				th {
					text-align: center;
					&:first-child {
						text-align: left;
					}
					&:last-child {
						text-align: right;
					}
				}
			}
			tbody {
				td {
					text-align: center;
					&:first-child {
						text-align: left;
					}
					&:last-child {
						text-align: right;
					}
				}
			}
		}
	}
</style>